import { HttpError } from '@nzyme/fetch-utils/HttpError.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { assert } from '@nzyme/utils/assert.js';
import { createLruCache } from '@nzyme/utils/createLruCache.js';
import debounce from 'lodash.debounce';
import { computed, markRaw, reactive, watch } from 'vue';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import { getChatNextEventSeq } from '@sigma/chat-common/utils/getChatNextEventSeq.js';
import { ActorConnectionService } from '../services/ActorConnectionService.js';
import { ChatLoader } from './ChatLoader.js';
const MAX_CACHE_SIZE = 100;
/**
 * Singleton service that manages all chat states.
 * Subscribes to actor events once globally and routes them to cached chats.
 * Caches chats with LRU eviction.
 */
export const ChatStore = defineService({
    name: 'ChatStore',
    deps: {
        actorClient: ActorClient,
        actorConnection: ActorConnectionService,
        chatLoader: ChatLoader,
        logger: Logger,
    },
    setup({ actorClient, actorConnection, chatLoader, logger }) {
        const cache = createLruCache({ maxSize: MAX_CACHE_SIZE });
        // One-shot open-time baselines recorded by the start flow (markOpening) and consumed once
        // by the chat view (consumeOpenedAt). Not keyed to the cache entry — its lifetime is a
        // single open, so a plain re-open of a cached chat correctly gets a fresh Date.now().
        const openedAtByChat = new Map();
        const chatRef = actorClient.actor(ChatActor);
        const connected = computed(() => actorConnection.connected);
        // Subscribe to actor events once globally
        chatRef.events.chatEvents.on(handleChatEvent);
        // On disconnect, mark all cached chats stale
        watch(connected, isConnected => {
            if (isConnected === false) {
                for (const entry of cache.values()) {
                    entry.stale = true;
                }
            }
        });
        return { getChat, refreshChat, seedChat, markOpening, consumeOpenedAt };
        function markOpening(chatId, openedAt) {
            openedAtByChat.set(String(chatId), openedAt);
        }
        function consumeOpenedAt(chatId) {
            const key = String(chatId);
            const openedAt = openedAtByChat.get(key);
            openedAtByChat.delete(key);
            return openedAt;
        }
        function getChat(chatId) {
            const existing = cache.get(chatId);
            if (existing) {
                return existing;
            }
            // Create new entry + view model
            const entry = createChatEntry(chatId);
            cache.set(chatId, entry);
            return entry;
        }
        async function refreshChat(chatId) {
            const entry = cache.get(chatId);
            if (!entry) {
                return;
            }
            await loadChat(chatId, entry);
        }
        function seedChat(chatId, events) {
            const existing = cache.get(chatId);
            if (existing?.loaded) {
                return;
            }
            const entry = existing ?? createChatEntry(chatId);
            if (!existing) {
                cache.set(chatId, entry);
            }
            const unconsumed = entry.applyEvents(events);
            if (unconsumed.length > 0) {
                entry.pending.push(...unconsumed);
            }
            entry.loaded = true;
            entry.stale = false;
            entry.error = null;
        }
        function handleChatEvent(params) {
            const chatId = String(params.actorId);
            const entry = cache.get(chatId);
            // Silently ignore events for uncached chats
            if (!entry) {
                return;
            }
            pushRemoteEvents(entry, params.event);
        }
        function createChatEntry(chatId) {
            const loadChatDebounced = debounce(() => loadChat(chatId, entry), 1000);
            const entry = reactive({
                events: [],
                pending: [],
                liveSeqs: markRaw(new Set()),
                loaded: false,
                stale: false,
                loading: null,
                error: null,
                applyEvents,
                // ChatViewModel interface fields
                chatId,
                connected,
                load,
                pushLocalEvent,
                pushRemoteEvent,
                reload,
                removeLocalEvents,
            });
            return entry;
            async function load() {
                // Idempotent: callers (component mount hooks, navigation guards)
                // may invoke load() unconditionally; skip the network round-trip
                // if the entry is already fresh (e.g. seeded by a start-chat flow).
                if (entry.loaded && !entry.stale) {
                    return;
                }
                await loadChat(chatId, entry);
            }
            function pushLocalEvent(event) {
                entry.events.push(mapEvent(event, { local: true }));
            }
            function pushRemoteEvent(event) {
                pushRemoteEvents(entry, event);
            }
            async function reload() {
                entry.loaded = false;
                entry.events.length = 0;
                entry.pending.length = 0;
                await loadChat(chatId, entry);
            }
            async function removeLocalEvents() {
                entry.events = entry.events.filter(e => !e.local);
                await loadChat(chatId, entry);
            }
            /**
             * Applies pending events to the chat state in order, without mutating
             * the input array. Returns the events that could not yet be applied
             * (because they are waiting for earlier seqs); the caller is responsible
             * for deciding whether to retain them for a later retry.
             */
            function applyEvents(events) {
                const sorted = events.toSorted((a, b) => a.seq - b.seq);
                for (let i = 0; i < sorted.length; i++) {
                    if (!applyEvent(sorted[i])) {
                        return sorted.slice(i);
                    }
                }
                return [];
            }
            /**
             * Applies a single event to the chat state.
             *
             * Handles three cases:
             * 1. Future message (seq > next expected) — wait for missing messages
             * 2. Next message (seq == next expected) — add new message
             * 3. Older message (seq < next expected) — update existing message
             */
            function applyEvent(event) {
                const nextMessageNum = getChatNextEventSeq(entry.events);
                if (event.seq > nextMessageNum) {
                    return false;
                }
                if (event.seq === nextMessageNum) {
                    entry.events.push(mapEvent(event, { live: entry.liveSeqs.has(event.seq) }));
                    return true;
                }
                const lastMessage = entry.events[entry.events.length - 1];
                assert(lastMessage);
                const existingIndex = entry.events.findIndex(m => m.seq === event.seq);
                if (existingIndex === -1) {
                    logger.error('Received older message not found in events', {
                        chatId,
                        seq: event.seq,
                        type: event.type,
                    });
                    void loadChatDebounced();
                    return true;
                }
                entry.events[existingIndex] = mapEvent(event, { live: entry.liveSeqs.has(event.seq) });
                return true;
            }
        }
        function pushRemoteEvents(entry, event) {
            const incoming = Array.isArray(event) ? event : [event];
            entry.pending.push(...incoming);
            // Record every live-delivered seq before applying. Keying by seq (not by which code
            // path finally applies the event) means a live arrival that lands as a gap now and is
            // only flushed later — via the load/pending reapply — still resolves to `live: true`.
            for (const e of incoming) {
                entry.liveSeqs.add(e.seq);
            }
            // applyEvents is non-destructive; explicitly replace pending with the
            // unconsumed remainder so the mutation intent is visible at the call site.
            entry.pending = entry.applyEvents(entry.pending);
        }
        async function loadChat(chatId, entry) {
            const promise = doLoadChat(chatId, entry);
            entry.loading = promise;
            try {
                await promise;
            }
            finally {
                if (entry.loading === promise) {
                    entry.loading = null;
                }
            }
        }
        async function doLoadChat(chatId, entry) {
            let fromSeq = 0;
            for (const event of entry.events) {
                if (event.local) {
                    break;
                }
                if (event.seq !== fromSeq) {
                    break;
                }
                fromSeq++;
            }
            try {
                const events = await chatLoader(chatId, fromSeq <= 0 ? undefined : fromSeq);
                if (!events) {
                    // The chat does not exist or is not accessible — the patient API collapses
                    // both into `null` so the client needs no exception handling for it.
                    markChatNotFound(chatId, entry);
                    return;
                }
                // Server-loaded events can contain gaps (e.g. when earlier seqs
                // are not yet visible); retain any unconsumed remainder alongside
                // pending events so they can be retried once the gap is filled.
                const unconsumed = entry.applyEvents(events);
                if (unconsumed.length > 0 || entry.pending.length > 0) {
                    entry.pending = entry.applyEvents([...unconsumed, ...entry.pending]);
                }
                entry.loaded = true;
                entry.stale = false;
                entry.error = null;
            }
            catch (error) {
                if (isChatNotFoundError(error)) {
                    markChatNotFound(chatId, entry);
                    return;
                }
                entry.error = { type: 'unknown', cause: error };
                logger.error('Failed to load chat', { chatId, error });
            }
        }
        /**
         * Not-found is an expected outcome (e.g. a stale link), so the entry is dropped from the
         * cache and nothing is logged at error level — it must not pollute Sentry.
         */
        function markChatNotFound(chatId, entry) {
            entry.error = { type: 'not-found' };
            cache.delete(chatId);
        }
        /**
         * Both APIs now answer an inaccessible chat with `null` (handled above); this stays as the
         * fallback for any other transport that reports it as a 400/403 instead.
         * The RPC client surfaces these as `RpcError` (JSON responses) or `FetchError` (otherwise),
         * both subclasses of `HttpError`, so narrowing on `HttpError` covers either case.
         */
        function isChatNotFoundError(error) {
            if (error instanceof HttpError) {
                return error.status === 403 || error.status === 400;
            }
            return false;
        }
        function mapEvent(event, options) {
            return { ...event, local: options?.local, live: options?.live };
        }
    },
});
//# sourceMappingURL=ChatStore.js.map